import { FC, ImgHTMLAttributes, useEffect, useState } from 'react';

import { fileBucketUrl } from '@shared/utils/fileUtils';

//* 버킷 이미지 <img> — 축소본으로 요청하고, 실패하면 폴백 축소본 → 원본(raw) 순으로 1단계씩 내려간다.
//*
//* 2026-10-23부터 축소본(w200/w400/w1080)은 키 그대로 내용만 WebP다(docs/image-transform-format-spec.md).
//* WebP를 못 읽는 브라우저(Safari/iOS 14 미만)는 디코드 실패로 onError가 나므로 그 자리에서 폴백으로 넘어간다.
//*   1) `${size}`      WebP 축소본
//*   2) `${size}-png`  폴백 축소본(PNG/JPEG, 백엔드 생성 요청 — 아직 없으면 404로 다음 단계)
//*   3) `raw`          원본(크지만 항상 있음)
//* 축소본 자체가 없는 옛 파일(파이프라인 이전 업로드)도 같은 사슬로 raw까지 내려간다(BucketImage와 같은 이유).
//* 구매자 화면용이라 antd Image(확대 미리보기) 없이 가볍게 둔다.

const FALLBACK_SUFFIX = '-png';

export const bucketImgCandidates = (size: string): string[] =>
  size === 'raw' ? ['raw'] : [size, `${size}${FALLBACK_SUFFIX}`, 'raw'];

//* WebP 디코드 가능 여부를 1회만 확인해 둔다(1×1 lossy WebP). 확인 전(null)엔 지원으로 간주하고 1단계부터 시작한다.
//* 미지원이면 깨진 아이콘이 잠깐 뜨는 1단계를 건너뛰어 바로 폴백부터 요청한다.
let webpSupport: boolean | null = null;
const webpListeners = new Set<(ok: boolean) => void>();

export const probeWebpSupport = (): boolean | null => {
  if (webpSupport !== null || typeof Image === 'undefined') return webpSupport;
  const img = new Image();
  const settle = (ok: boolean) => {
    webpSupport = ok;
    webpListeners.forEach((l) => l(ok));
    webpListeners.clear();
  };
  img.onload = () => settle(img.width > 0 && img.height > 0);
  img.onerror = () => settle(false);
  img.src =
    'data:image/webp;base64,UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA';
  return webpSupport;
};

//* 테스트용: 판정 결과를 비운다
export const resetWebpSupportForTest = () => {
  webpSupport = null;
  webpListeners.clear();
};

const useWebpSupport = (): boolean | null => {
  const [support, setSupport] = useState<boolean | null>(() => probeWebpSupport());
  useEffect(() => {
    if (webpSupport !== null) {
      setSupport(webpSupport);
      return;
    }
    webpListeners.add(setSupport);
    return () => {
      webpListeners.delete(setSupport);
    };
  }, []);
  return support;
};

interface Props extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  /** 버킷 키 */
  value: string;
  /** 축소본 크기 (기본 w1080 — 폰 해상도에서 상세 이미지로 충분) */
  size?: string;
}

export const BucketImg: FC<Props> = function BucketImg({
  value,
  size = 'w1080',
  onError,
  ...rest
}) {
  const webp = useWebpSupport();
  const candidates = bucketImgCandidates(size);
  //* 사슬에서 현재 단계. WebP 미지원이 확정되면 1단계(WebP)는 건너뛴다.
  const [stage, setStage] = useState(0);
  //* 키나 크기가 바뀌면 처음부터(캐러셀 재사용)
  useEffect(() => setStage(0), [value, size]);
  const effectiveStage =
    webp === false && stage === 0 && candidates.length > 1 ? 1 : stage;
  const type = candidates[Math.min(effectiveStage, candidates.length - 1)] ?? 'raw';
  return (
    <img
      {...rest}
      src={fileBucketUrl(value, type)}
      onError={(e) => {
        if (effectiveStage < candidates.length - 1) setStage(effectiveStage + 1);
        onError?.(e);
      }}
    />
  );
};
